import React from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const HIGHLIGHTED_CODE_CLASS = 'markdown-highlighted-code';

interface MarkdownContentProps {
  content: string;
  accentColor?: 'primary' | 'secondary';
  variant?: 'inline' | 'document';
  syntaxHighlight?: boolean;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  accentColor = 'primary',
  variant = 'inline',
  syntaxHighlight = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const codeStyle = isDark ? oneDark : oneLight;
  const isDocument = variant === 'document';
  // A document sets its own text colour; inline content takes the colour of what holds it, such
  // as a chat bubble whose palette differs from the page's.
  const textColor = isDocument ? theme.palette.text.primary : 'inherit';

  const baseSx = {
    // The box may shrink below its widest child, and an unbroken token such as an ARN wraps
    // instead of running past the edge; tables and code scroll inside their own box below.
    minWidth: 0,
    overflowWrap: 'anywhere',
    '& p': {
      mb: 1.5,
      lineHeight: 1.6,
      color: textColor,
    },
    '& p:last-child': { mb: 0 },
    '& ul, & ol': {
      mb: 1.5,
      pl: 2.5,
      color: textColor,
    },
    '& li': { mb: 0.5 },
    '& code': {
      backgroundColor: isDark
        ? alpha(theme.palette.common.white, 0.1)
        : alpha(theme.palette.common.black, 0.06),
      color: isDark
        ? theme.palette[accentColor].light
        : theme.palette[accentColor].dark,
      padding: '2px 6px',
      borderRadius: '4px',
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
    '& pre': {
      backgroundColor: theme.palette.surface.codeBlock,
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      overflowX: 'auto',
      maxWidth: '100%',
      mb: 1.5,
    },
    '& pre code': {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      padding: 0,
    },
    // The highlighter colours its own code element; only the inline-code box above is undone.
    [`& .${HIGHLIGHTED_CODE_CLASS} code`]: {
      padding: 0,
      borderRadius: 0,
      fontSize: 'inherit',
    },
    '& strong': {
      fontWeight: 600,
      color: textColor,
    },
    // Cells keep normal wrapping: "anywhere" would shrink every column to one character
    // instead of letting the table scroll inside its wrapper.
    '& th, & td': { overflowWrap: 'normal' },
    '& blockquote': {
      borderLeft: '4px solid',
      borderColor: theme.palette[accentColor].main,
      backgroundColor: theme.palette.surface.blockquote,
      pl: 2,
      py: 0.5,
      my: 1.5,
      ml: 0,
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
    },
  } as const;

  const headingColor = textColor;

  const variantSx = {
    '& h1': isDocument
      ? { mt: 0, mb: 3, fontSize: '2rem', fontWeight: 600, borderBottom: 1, borderColor: 'divider', pb: 1, color: headingColor }
      : { mt: 2, mb: 1, fontSize: '1.5rem', fontWeight: 600, color: headingColor },
    '& h2': isDocument
      ? { mt: 4, mb: 2, fontSize: '1.5rem', fontWeight: 600, color: headingColor }
      : { mt: 2, mb: 1, fontSize: '1.3rem', fontWeight: 600, color: headingColor },
    '& h3': isDocument
      ? { mt: 3, mb: 1.5, fontSize: '1.25rem', fontWeight: 600, color: headingColor }
      : { mt: 2, mb: 1, fontSize: '1.1rem', fontWeight: 600, color: headingColor },
    '& h4': { mt: 2, mb: 1, fontSize: '1.1rem', fontWeight: 600, color: headingColor },
    ...(isDocument && {
      '& table': { borderCollapse: 'collapse' as const, width: '100%' },
      '& th, & td': { border: 1, borderColor: 'divider', p: 1, textAlign: 'left' as const },
      '& th': { bgcolor: theme.palette.surface.tableHeader, fontWeight: 'bold' },
      '& hr': { my: 3, borderColor: 'divider' },
      '& a': { color: theme.palette.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
    }),
  };

  const components: Components = {
    table: ({ node: _node, ...props }) => (
      <Box sx={{ overflowX: 'auto', maxWidth: '100%', mb: isDocument ? 2 : 1.5 }}>
        <table {...props} />
      </Box>
    ),
    // A fenced block with a language renders as the highlighter's own box in place of the styled
    // pre, so it is not boxed twice; a fence without one keeps the styled pre.
    ...(syntaxHighlight && {
      pre({ node: _node, children, ...props }) {
        const code = React.isValidElement<{ className?: string; children?: React.ReactNode }>(children)
          ? children
          : null;
        const match = /language-(\w+)/.exec(code?.props.className || '');
        if (!code || !match) return <pre {...props}>{children}</pre>;
        return (
          <SyntaxHighlighter
            style={codeStyle}
            language={match[1]}
            PreTag="div"
            className={HIGHLIGHTED_CODE_CLASS}
            customStyle={{ margin: 0, overflowX: 'auto', maxWidth: '100%' }}
          >
            {String(code.props.children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        );
      },
    }),
  };

  return (
    <Box
      sx={[baseSx, variantSx]}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownContent;
